import { all, dropTask } from 'ember-concurrency';
import { query } from '@warp-drive/utilities/json-api';
import { saveRecord } from 'tio-data/requests/save-record';
import {
  createCardHolderRequest,
  type CreateCardHolderBody,
} from 'tio-data/requests/tas-application-actions';
import ActionableTranslation from 'tio-ui/components/actionable-translation';
import Button from 'tio-ui/components/button';
import Flatpickr from 'tio-common/components/flatpickr';
import { flatten, safeParse } from 'valibot';
import { formatDateToISO } from 'tio-common/utils/date';
import { getSingleValueForTasField } from 'tio-common/utils/tuition-assistance/fields';
import { htmlSafe } from '@ember/template';
import { on } from '@ember/modifier';
import Input from 'tio-ui/components/input';
import { or, join } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import { VStack } from 'tio-ui/components/layout';
import * as v from 'valibot';
import AcceptConditions from 'tio-employee/components/tuition-assistance/forms/accept-conditions';
import Component from '@glimmer/component';
import errorsForValibotField from 'tio-common/helpers/errors-for-valibot-field';
import type { Agreement as AgreementModel } from 'tio-data/data/resources/agreement.type';
import type { ConditionExtension as ConditionModel } from 'tio-data/data/resources/condition.ext';
import type { PcsCardHolder as PcsCardHolderModel } from 'tio-data/data/resources/pcs-card-holder.type';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type Owner from '@ember/owner';
import type SessionContextService from 'tio-employee/services/session-context';
import type { Store } from '@warp-drive/core';

interface TasCardHolderArgs {
  Args: {
    application: TasApplicationModel;
  };
  Element: HTMLDivElement;
}

type FormDataType = {
  firstName: string;
  lastName: string;
  employeeDob: string;
  conditionAccepted: boolean[];
};

export default class TasCardHolderDrawer extends Component<TasCardHolderArgs> {
  @tracked formData: FormDataType = {
    firstName: '',
    lastName: '',
    employeeDob: '',
    conditionAccepted: [],
  };

  @tracked errors: Record<string, string[]> = {};
  @tracked acceptedConditions: ConditionModel[] = [];
  @tracked hasSubmitted = false;
  @tracked cardHolderCreated = false;
  @service declare sessionContext: SessionContextService;
  @service declare store: Store;

  constructor(owner: Owner, args: TasCardHolderArgs['Args']) {
    super(owner, args);
    const employee = this.args.application.tasProgramInstance.employee;
    const employeeMeta = employee.meta?.merged;
    this.formData.firstName = (employeeMeta?.firstName as string) ?? employee.firstName ?? '';
    this.formData.lastName = (employeeMeta?.lastName as string) ?? employee.lastName ?? '';

    const metaDob =
      employeeMeta?.birthDate || getSingleValueForTasField('EMPLOYEE_DOB', args.application.fields);
    this.formData.employeeDob = metaDob ? new Date(metaDob).toISOString().slice(0, 10) : '';
  }

  fetchConditions = trackedFunction(this, async (): Promise<ConditionModel[]> => {
    const { content } = (await this.store.request(
      query<ConditionModel>('condition', {
        'filter[province]': 'tio_card',
        'filter[classification]': 'tio_card_terms_of_use',
      })
    )) as { content: { data: ConditionModel[] } };
    return content.data;
  });

  get conditionsForSubmit() {
    return this.fetchConditions.value || [];
  }

  get cardHolderExistsForEmployee() {
    return this.args.application.tasProgramInstance.employee?.pcsCardHolders.length > 0;
  }

  get needsCardHolderForDirectPay() {
    return !this.cardHolderExistsForEmployee || !this.cardHolderCreated;
  }

  min18YearsOld() {
    const now = new Date();
    return new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());
  }

  max90YearsOld() {
    const now = new Date();
    return new Date(now.getFullYear() - 90, now.getMonth(), now.getDate());
  }

  handleFormChange = (_event: Event) => {
    const form = _event.currentTarget as HTMLFormElement;
    const fd = new FormData(form);
    const firstName = (fd.get('firstName') as string) ?? this.formData.firstName;
    const lastName = (fd.get('lastName') as string) ?? this.formData.lastName;
    this.formData = {
      ...this.formData,
      firstName,
      lastName,
    };
  };

  updateEmployeeDob = (selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      const isoDate = formatDateToISO(selectedDates[0]);
      this.formData = { ...this.formData, employeeDob: isoDate };
    }
  };

  handleFormSubmit = (_event: SubmitEvent) => {
    _event.preventDefault();
    this.submitForm();
  };

  didUpdateAcceptedConditions = (conditions: ConditionModel[] = []) => {
    this.acceptedConditions = conditions;
    this.formData = {
      ...this.formData,
      conditionAccepted: conditions.map(() => true),
    };
  };

  saveAgreementsForConditions = dropTask(async () => {
    const user = this.sessionContext.user;
    const agreements = this.acceptedConditions.map((condition: ConditionModel) => {
      return this.store.createRecord<AgreementModel>('agreement', {
        user: user,
        condition: condition,
      });
    });
    await all(agreements.map((agreement: AgreementModel) => saveRecord(this.store, agreement)));
    return agreements;
  });

  get formSchema() {
    return v.object({
      firstName: v.pipe(v.string('First name is required'), v.nonEmpty('First name is required')),
      lastName: v.pipe(v.string('Last name is required'), v.nonEmpty('Last name is required')),
      employeeDob: v.pipe(
        v.string('Date of birth is required'),
        v.nonEmpty('Date of birth is required'),
        v.transform((input) => new Date(input)),
        v.date('Date of birth must be a valid date'),
        v.maxValue(this.min18YearsOld(), 'You must be at least 18 years old'),
        v.minValue(this.max90YearsOld(), 'You must be younger than 90 years old')
      ),
      conditionAccepted: v.pipe(
        v.union([v.boolean(), v.array(v.boolean())]),
        v.transform((input) => (Array.isArray(input) ? input : [input])),
        v.array(v.boolean()),
        v.minLength(this.conditionsForSubmit.length, 'You must accept all terms'),
        v.custom((arr) => (arr as boolean[]).every(Boolean), 'You must accept all terms')
      ),
    });
  }

  get formValidationErrors() {
    const result = safeParse(this.formSchema, this.formData, { abortEarly: false });
    if (!result.success) {
      const flattened = flatten(result.issues);
      return flattened.nested ?? {};
    }
    return {};
  }

  get hasFormValidationErrors() {
    const errors = this.formValidationErrors;
    return Object.keys(errors || {}).length > 0;
  }

  createCardHolderTask = dropTask(async (payload: CreateCardHolderBody) => {
    // WarpDrive mutation builder (Phase 2.7) — replaces
    // store.adapterFor('tas-application').createCardHolder(application, payload).
    // The legacy adapter POSTed the snake_case body, pushPayload'd the response,
    // peeked the resulting pcs-card-holder by id and linked it to the employee
    // belongsTo. Routing through store.request materializes the record off
    // content.data; we re-do the employee link to preserve cache behavior.
    const application = this.args.application;
    const { content } = (await this.store.request(
      createCardHolderRequest(application.id as string, payload)
    )) as { content: { data: PcsCardHolderModel | null } };

    const cardHolder = content.data;
    const cardHolderId = cardHolder?.id;
    const employeeId = application.tasProgramInstance.employee?.id;
    if (cardHolderId && employeeId) {
      // Link the employee into the WarpDrive cache for this card-holder. The
      // legacy EmberData reference API (`cardHolder.belongsTo('employee').push`)
      // no longer exists post-migration and threw "belongsTo is not a function"
      // in production; push the relationship as a JSON:API document instead.
      this.store.push({
        data: {
          id: cardHolderId,
          type: 'pcs-card-holder',
          relationships: {
            employee: { data: { type: 'employee', id: employeeId } },
          },
        },
      });
    }
    return cardHolder;
  });

  async submitForm() {
    this.hasSubmitted = true;
    // Guard: conditions must be loaded (and at least one must exist) before submitting.
    // Without this, minLength(0) would allow submit with no accepted terms and
    // agreement_id would be undefined, causing the adapter to throw.
    if (this.conditionsLoading || this.conditionsForSubmit.length === 0) {
      return;
    }
    if (this.hasFormValidationErrors) {
      return;
    }

    try {
      const savedAgreements = await this.saveAgreementsForConditions.perform();

      const firstAgreementId =
        Array.isArray(savedAgreements) && savedAgreements[0]?.id
          ? savedAgreements[0].id
          : undefined;

      // Mirrors the legacy adapter guard (`!payload.agreement_id` → throw): the
      // builder body requires a string agreement_id.
      if (!firstAgreementId) {
        throw new Error('Missing required parameters');
      }

      const payload: CreateCardHolderBody = {
        legal_first_name: this.formData.firstName,
        legal_last_name: this.formData.lastName,
        dob: this.formData.employeeDob,
        agreement_id: firstAgreementId,
      };

      await this.createCardHolderTask.perform(payload);
      this.cardHolderCreated = true;
    } catch (error) {
      console.error('Error creating card holder:', error);
    }
  }

  get conditionsLoading() {
    return this.fetchConditions.isLoading;
  }

  get submitTaskRunning() {
    return (
      this.conditionsLoading ||
      this.saveAgreementsForConditions.isRunning ||
      this.createCardHolderTask.isRunning
    );
  }

  <template>
    {{#if this.needsCardHolderForDirectPay}}
      <form
        class="rounded-md tio-bg-sunken"
        {{on "change" this.handleFormChange}}
        {{on "submit" this.handleFormSubmit}}
      >
        <VStack>
          <div>
            <h2 class="text-lg font-medium">{{t "tas.card_holder.default"}}</h2>
            <p class="text-sm">
              {{htmlSafe (t "tas.card_holder.subheader")}}
            </p>
          </div>

          <div>
            <label class="font-semibold tio-fg-secondary tio-row-extra-tight">
              {{t "tas.card_holder.name"}}
              <span class="tio-fg-danger" aria-hidden="true">*</span>
            </label>
          </div>

          {{! First name input }}
          <div class="flex gap-x-6 max-w-md">
            <Input
              @name="firstName"
              data-test="legal-first-name"
              @value={{this.formData.firstName}}
              @classes={{hash base="tas-course-cash-label !font-semibold grow"}}
              @errors={{errorsForValibotField
                "firstName"
                schemaErrors=this.formValidationErrors
                hasSubmitted=this.hasSubmitted
              }}
              placeholder={{t "tas.card_holder.first_name"}}
              disabled={{true}}
            />

            {{! Last name input }}
            <Input
              @name="lastName"
              data-test="legal-last-name"
              @value={{this.formData.lastName}}
              @classes={{hash base="tas-course-cash-label !font-semibold grow"}}
              @errors={{errorsForValibotField
                "lastName"
                schemaErrors=this.formValidationErrors
                hasSubmitted=this.hasSubmitted
              }}
              placeholder={{t "tas.card_holder.last_name"}}
              disabled={{true}}
            />
          </div>

          {{! Date of birth input }}
          <div class="max-w-md tio-col-extra-tight">
            <label for="EMPLOYEE_DOB" class="font-semibold tio-fg-secondary">
              {{t "tas.card_holder.date_of_birth"}}
              <span class="tio-fg-danger">*</span>
            </label>
            <Flatpickr
              @date={{or this.formData.employeeDob ""}}
              @onChange={{this.updateEmployeeDob}}
              @dateFormat="Y-m-d"
              @altInput={{true}}
              @altFormat="F j, Y"
              @disableMobile={{true}}
              placeholder="mm/dd/yyyy"
              data-test="employee-dob"
              class="tio-form-input-lg font-semibold"
            />
            {{#let
              (errorsForValibotField
                "employeeDob" schemaErrors=this.formValidationErrors hasSubmitted=this.hasSubmitted
              )
              as |errors|
            }}
              {{#if errors}}
                <div class="tio-fg-danger text-sm mt-1">
                  {{join errors ", "}}
                </div>
              {{/if}}
            {{/let}}
          </div>

          <div>
            <label class="font-semibold tio-fg-secondary tio-row-extra-tight">
              {{t "tas.card_holder.terms_and_conditions"}}
              <span class="tio-fg-danger" aria-hidden="true">*</span>
            </label>
            <AcceptConditions
              @conditions={{this.conditionsForSubmit}}
              @onChange={{this.didUpdateAcceptedConditions}}
              @errors={{this.formValidationErrors}}
              @hasSubmitted={{this.hasSubmitted}}
            />
          </div>

          <Button
            @intent="primary"
            class="p-10"
            data-test="tas-card-holder-submit"
            type="submit"
            @isRunning={{this.submitTaskRunning}}
          >
            {{t "common.submit"}}
          </Button>
        </VStack>
      </form>
      <div class="tio-fg-secondary text-xs py-2">
        <ActionableTranslation
          @t="tas.card_holder.footer_text"
          @links={{hash
            termsOfUse=(hash
              href="https://stripe.com/legal/issuing/celtic-authorized-user-terms"
              t="tas.card_holder.footer_text_terms_link"
              target="_blank"
              rel="noopener noreferrer"
            )
          }}
        />
      </div>
    {{/if}}
  </template>
}
